import { Injectable, inject } from '@angular/core';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot, CanActivateFn } from '@angular/router';
import { RestService } from './app/rest.service';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  private exemptedRoutes: string[] = [
    'login',
    'forget-password',
    'reset-password',
    'notFound',
    'pages',
  ];
  private readonly SESSION_STORAGE_KEY = 'firstApiCallTimestamp';
  private readonly TOKEN_STORAGE_KEY = 'token';
  private readonly EXPIRATION_DAYS = 4;
  private roleAccessJSONArray: any[] = [];

  constructor(private router: Router, private restService: RestService) { }

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    if (this.isExemptedRoute(next.routeConfig.path)) {
      return of(true);
    }
    const userId = localStorage.getItem('userId');
    const token = localStorage.getItem(this.TOKEN_STORAGE_KEY);
    const storedToken = sessionStorage.getItem(this.TOKEN_STORAGE_KEY);

    if (userId) {
      const currentTime = new Date().getTime();
      const firstApiCallTimestamp = sessionStorage.getItem(this.SESSION_STORAGE_KEY);
      const isWithinExpiration = firstApiCallTimestamp && (currentTime - parseInt(firstApiCallTimestamp) < this.EXPIRATION_DAYS * 24 * 60 * 60 * 1000);
      const tokenChanged = storedToken !== token;

      const storedRoleAccessJSONArray = sessionStorage.getItem('roleAccessJSONArray');
      if (storedRoleAccessJSONArray) {
        this.roleAccessJSONArray = JSON.parse(storedRoleAccessJSONArray);
      }

      if (!firstApiCallTimestamp || !isWithinExpiration || tokenChanged) {
        const roleId = localStorage.getItem('roleId') ? localStorage.getItem('roleId') : '';
        let obj: any = { accessName: next.routeConfig.path, apiAccessId: parseInt(roleId) };

        return this.restService.postModel(obj, 'IsRoutePathAccessEnabledForRoleId').pipe(
          map((response) => {
            if (response) {
              this.roleAccessJSONArray = response.roleAccessJSONArray;
              sessionStorage.setItem('roleAccessJSONArray', JSON.stringify(this.roleAccessJSONArray)); // Store in sessionStorage
              sessionStorage.setItem(this.SESSION_STORAGE_KEY, currentTime.toString());
              sessionStorage.setItem(this.TOKEN_STORAGE_KEY, token);

              const hasAccess = this.roleAccessJSONArray.some((roleAccessObject: any) => next.routeConfig.path === roleAccessObject.path);
              if (hasAccess) {
                return true;
              } else {
                this.router.navigate(['pages/**']);
                return false;
              }
            } else {
              return true;
            }
          }),
          catchError((error) => {
            console.error('Error occurred while checking permissions', error);
            this.router.navigate(['auth/login']);
            return of(false);
          })
        );
      } else {
        const hasAccess = this.roleAccessJSONArray.some((roleAccessObject: any) => next.routeConfig.path === roleAccessObject.path);
        if (hasAccess) {
          return of(true);
        } else {
          this.router.navigate(['pages/**']);
          return of(false);
        }
      }
    }
    this.router.navigate(['auth/login']);
    return of(false);
  }

  private isExemptedRoute(path: string): boolean {
    return this.exemptedRoutes.includes(path);
  }

  getRoleAccessJSONArray(): any[] {
    return this.roleAccessJSONArray;
  }
}

export const AuthGuard: CanActivateFn = (next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> => {
  return inject(PermissionService).canActivate(next, state);
}